import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const fetcher = (url) => api.get(url).then((r) => r.data);

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
};

export default function Admin() {
  const { user } = useAuth();
  const { data: users, error } = useSWR("/admin/users", fetcher);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [duration, setDuration] = useState("1_month");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  if (error?.response?.status === 403) {
    return <div className="p-10 text-center text-muted-foreground">Akses ditolak. Halaman ini hanya untuk admin.</div>;
  }

  const invite = async () => {
    if (!email.trim() || !name.trim()) return toast.error("Email dan nama wajib");
    try {
      await api.post("/admin/users", {
        email: email.trim(),
        name: name.trim(),
        role,
        duration
      });
      toast.success("User dibuat. Mereka bisa login dengan Google email tersebut.");
      mutate("/admin/users");
      setOpen(false);
      setEmail("");
      setName("");
      setRole("user");
      setDuration("1_month");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    }
  };

  const del = async (uid) => {
    try {
      await api.delete(`/admin/users/${uid}`);
      mutate("/admin/users");
      toast.success("Dihapus");
    } catch {
      toast.error("Gagal");
    }
  };

  const handleConfirmDelete = () => {
    if (confirmId) {
      del(confirmId);
      setConfirmId(null);
    }
    setConfirmOpen(false);
  };

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-ai" />
            <p className="label-caps">Admin Panel</p>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-1">User Management</h1>
          <p className="text-sm text-muted-foreground mt-2">Kelola daftar user yang bisa mengakses Aliran dan masa aktif langganan mereka.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-foreground text-background hover:bg-foreground/90 h-11" data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-2" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Add User</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground -mt-2">
              Whitelist a Google account. Setelah didaftarkan, mereka bisa masuk lewat tombol "Masuk dengan Google" menggunakan email tersebut.
            </p>
            <div className="space-y-4 py-2">
              <div>
                <Label>Google Email</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-11" placeholder="teman@gmail.com" data-testid="user-email-input" />
              </div>
              <div>
                <Label>Nama Tampilan</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-11" data-testid="user-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="mt-2 h-11" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User (regular)</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Masa Langganan</Label>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1_month">1 Bulan</SelectItem>
                      <SelectItem value="3_months">3 Bulan</SelectItem>
                      <SelectItem value="6_months">6 Bulan</SelectItem>
                      <SelectItem value="1_year">1 Tahun</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={invite} className="bg-foreground text-background" data-testid="invite-user-submit">Add User</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-12 label-caps px-4 py-3 border-b border-border bg-secondary/40">
          <div className="col-span-3">User</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">Status Langganan</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {(users || []).map((u) => (
          <div key={u.user_id} className="grid grid-cols-12 px-4 py-3 border-b border-border last:border-0 items-center hover:bg-accent/40" data-testid={`user-row-${u.user_id}`}>
            <div className="col-span-3 flex items-center gap-3">
              {u.picture ? <img src={u.picture} className="h-8 w-8 rounded-full" alt="" /> : <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-xs font-medium">{u.name?.[0]}</div>}
              <span className="text-sm font-medium">{u.name}</span>
            </div>
            <div className="col-span-3 text-sm text-muted-foreground truncate">{u.email}</div>
            <div className="col-span-2">
              <span className={`text-xs px-2 py-1 border ${u.role === "admin" ? "border-ai text-ai" : "border-border text-muted-foreground"}`}>{u.role}</span>
            </div>
            <div className="col-span-2 text-sm text-muted-foreground">
              {u.role === "admin" ? (
                <span className="text-xs text-ai font-medium">Seterusnya (Admin)</span>
              ) : (
                <span className={new Date(u.subscription_expires_at) < new Date() ? "text-destructive font-medium" : ""}>
                  {new Date(u.subscription_expires_at) < new Date() ? "Expired (" + formatDate(u.subscription_expires_at) + ")" : formatDate(u.subscription_expires_at)}
                </span>
              )}
            </div>
            <div className="col-span-2 text-right">
              {u.user_id !== user?.user_id && (
                <Button variant="ghost" size="icon" onClick={() => { setConfirmId(u.user_id); setConfirmOpen(true); }} data-testid={`user-delete-${u.user_id}`}><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Konfirmasi Hapus</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-sm text-muted-foreground">
            Apakah Anda yakin ingin menghapus user ini dari sistem?
          </div>
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Batal</Button>
            <Button variant="destructive" className="bg-loss text-white hover:bg-loss/90" onClick={handleConfirmDelete}>Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
